import Web3 from 'web3';
import { AbiItem } from 'web3-utils';

export interface ShiboshiInfo {
  ids: number[];
  startTime: number;
  ogUser: string;
  numDays: number;
}

const abi = [
  {
    inputs: [
      { internalType: 'address', name: '_shiboshi', type: 'address' },
      { internalType: 'uint256', name: 'amountMin', type: 'uint256' },
      { internalType: 'uint256', name: 'amountMax', type: 'uint256' },
      { internalType: 'uint256', name: 'daysMin', type: 'uint256' },
      { internalType: 'uint256', name: 'daysMax', type: 'uint256' },
    ],
    stateMutability: 'nonpayable',
    type: 'constructor',
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: 'address', name: 'previousOwner', type: 'address' },
      { indexed: true, internalType: 'address', name: 'newOwner', type: 'address' },
    ],
    name: 'OwnershipTransferred',
    type: 'event',
  },
  {
    inputs: [],
    name: 'AMOUNT_MAX',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'AMOUNT_MIN',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'DAYS_MAX',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'DAYS_MIN',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'SHIBOSHI',
    outputs: [{ internalType: 'contract IERC721', name: '', type: 'address' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'address', name: 'user', type: 'address' },
      { internalType: 'uint256', name: 'targetWeight', type: 'uint256' },
    ],
    name: 'extraDaysNeeded',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'address', name: 'user', type: 'address' },
      { internalType: 'uint256', name: 'targetWeight', type: 'uint256' },
    ],
    name: 'extraShiboshiNeeded',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'address', name: 'user', type: 'address' }],
    name: 'isWinner',
    outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'landAuction',
    outputs: [{ internalType: 'contract ILandAuction', name: '', type: 'address' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'uint256[]', name: 'ids', type: 'uint256[]' },
      { internalType: 'uint256', name: 'numDaysToAdd', type: 'uint256' },
    ],
    name: 'lock',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'address', name: 'user', type: 'address' }],
    name: 'lockInfoOf',
    outputs: [
      { internalType: 'uint256[]', name: 'ids', type: 'uint256[]' },
      { internalType: 'uint256', name: 'startTime', type: 'uint256' },
      { internalType: 'uint256', name: 'numDays', type: 'uint256' },
      { internalType: 'address', name: 'ogUser', type: 'address' },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'owner',
    outputs: [{ internalType: 'address', name: '', type: 'address' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'renounceOwnership',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'address', name: 'sale', type: 'address' }],
    name: 'setLandAuction',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'address', name: 'newOwner', type: 'address' }],
    name: 'transferLock',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'address', name: 'newOwner', type: 'address' }],
    name: 'transferOwnership',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  { inputs: [], name: 'unlock', outputs: [], stateMutability: 'nonpayable', type: 'function' },
  {
    inputs: [{ internalType: 'address', name: 'user', type: 'address' }],
    name: 'unlockAt',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'address', name: 'user', type: 'address' }],
    name: 'weightOf',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
];

const address = process.env.NEXT_PUBLIC_LOCKSHIBOSHI_ADDRESS;

const lockInfoOf = async (wallet: string, provider: any): Promise<ShiboshiInfo> => {
  const web3 = new Web3(provider);
  var contract = new web3.eth.Contract(abi as AbiItem[], address);
  const { ids, startTime, ogUser, numDays } = {
    ...((await contract.methods.lockInfoOf(wallet).call()) as ShiboshiInfo),
  };
  let result: ShiboshiInfo = {
    ids: ids,
    numDays: numDays,
    ogUser: ogUser,
    startTime: startTime,
  };
  return result;
};

const unlock = async (wallet: string, provider: any): Promise<any> => {
  const web3 = new Web3(provider);
  var contract = new web3.eth.Contract(abi as AbiItem[], address);
  const tx = await web3.eth.sendTransaction({
    from: wallet,
    to: address,
    data: contract.methods.unlock().encodeABI(),
    value: 0,
  });
  return await web3.eth.getTransactionReceipt(tx.transactionHash);
};

export const LockShiboshiService = {
  lockInfoOf,
  unlock,
};
